import { type ReactNode, type FC } from 'react';
import { twMerge } from 'tailwind-merge';

const variantDefaultLineHeight = {
    h1: '',
    h2: '',
    p: 'leading-[1.4em]',
    small: 'leading-[1.4em]',
    div: 'leading-[1.4em]',
};

const variantBaseClasses = {
    h1: 'text-[25px] font-semibold text-black',
    h2: 'text-lg font-semibold text-black',
    p: 'text-base text-slate-grey',
    small: 'text-xs text-slate-grey',
    div: 'text-base text-slate-grey',
};

type Props = {
    as?: keyof typeof variantBaseClasses;
    variant?: keyof typeof variantBaseClasses;
    className?: string;
    children: ReactNode;
    color?: 'black' | 'slate-grey' | 'white' | 'purple';
    uppercase?: boolean;
    weight?: 'normal' | 'medium' | 'semibold' | 'bold';
};

// This is only needed because if we set a default line height in variantBaseClasses, any
// text-* class will override it. So this adds back the default line height if it's not
// specifically overridden.
const maybeGetDefaultLineHeight = (variantOrComponent: keyof typeof variantBaseClasses, className?: string) => {
    const defaultLineHeight = variantDefaultLineHeight[variantOrComponent];

    // Unless the className includes an unprefixed leading-* class, apply the default line height
    return /(^|\s)leading-[^\s]+/.test(className ?? '') ? '' : defaultLineHeight;
};

export const Text: FC<Props> = ({
    // If no variant is provided, then the variant will default to the value of `as`. For accessibility
    // reasons, we support having `as` and `variant` be different, meaning that the tag type will not
    // match the styling. For more details about when you'd want to use that, see https://trello.com/c/SbjQGEJS#comment-66ec4684b8d004e18a425339
    as: Component = 'div',
    variant,
    uppercase = false,
    color,
    children,
    className,
    weight,
}) => (
    <Component
        className={twMerge(
            variantBaseClasses[variant || Component],
            'has-paragraph-margin',
            color && `text-${color}`,
            uppercase && 'uppercase',
            weight && `font-${weight}`,
            className,
            // This should come after className
            maybeGetDefaultLineHeight(variant || Component, className),
        )}
    >
        {children}
    </Component>
);
